import type {
  ReactNode,
  MouseEventHandler,
  CSSProperties,
  FC,
  ButtonHTMLAttributes,
  KeyboardEvent
} from 'react';
import { useRef, useState, Fragment } from 'react';
import classnames from 'classnames';
import {
  useFloating,
  arrow,
  autoUpdate,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  offset,
  useClick,
  FloatingFocusManager,
  shift,
  size,
  useTransitionStatus,
  FloatingPortal
} from '@floating-ui/react';

import arrowIcon from 'app/icons/arrow.svg';
import { getOffset, getPlacement } from 'app/utils/floating-ui';
import type { ButtonVariation } from 'app/components/Button';
import Button from 'app/components/Button';
import Icon from 'app/components/Icon';

import style from './style.scss';

const ARROW_UP_KEY = 'ArrowUp';
const ARROW_DOWN_KEY = 'ArrowDown';

interface Props {
  label: ReactNode;
  children: ReactNode;
  loading?: boolean;
  variation?: ButtonVariation;
  alignment?: Alignment;
  position?: Position;
  width?: number; // use to avoid freeze on chronium based browser on windows and linux
  offset?: { x: number; y: number };
  disabled?: boolean;
  className?: string;
  openedClassName?: string;
  portalClassName?: string;
  onOpenChange?: (becomeOpened: boolean) => void;
  opened?: boolean;
  withIcon?: boolean;
  dropdownClassName?: string;
  withArrow?: boolean;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  style?: CSSProperties;
  initialFocus?: number;
  withPortal?: boolean;
  changeFocusOnArrowDown?: boolean;
  compact?: boolean;
}

const Dropdown: FC<Props> = ({
  label,
  children,
  loading,
  alignment = 'start',
  position = 'bottom',
  width,
  offset: definedOffset,
  disabled = false,
  variation,
  className,
  openedClassName = '',
  portalClassName,
  onOpenChange,
  withIcon = true,
  dropdownClassName,
  withArrow = false,
  onMouseLeave,
  opened: forceOpened,
  style: css,
  initialFocus = -1, // default from floating-ui doc
  withPortal = false,
  changeFocusOnArrowDown = false,
  compact
}) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);

  const opened = forceOpened === undefined ? open : forceOpened;

  const { x, y, reference, floating, strategy, context, placement, middlewareData } = useFloating({
    middleware: [
      offset(getOffset(definedOffset, position)),
      flip(),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`
          });
        }
      }),
      shift(),
      arrow({ element: arrowRef })
    ],
    open: opened,
    onOpenChange: onOpenChange ?? setOpen,
    whileElementsMounted: autoUpdate,
    placement: getPlacement(position, alignment)
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const { isMounted, status } = useTransitionStatus(context, { duration: 300 });

  const { x: arrowX, y: arrowY } = middlewareData?.arrow || { x: undefined, y: undefined };

  const buttonClassName = classnames(className, {
    [style.emptyButton]: variation === 'empty',
    [openedClassName]: openedClassName && open
  });
  const arrowClassName = classnames(style.arrowIcon, {
    [style.arrowIconInput]: variation === 'input',
    [style.arrowIconInputSolo]: !label
  });

  const portalPositionnedClassName = classnames(
    withArrow
      ? {
          [style.top]: placement.includes('top'),
          [style.left]: placement.includes('left'),
          [style.right]: placement.includes('right'),
          [style.bottom]: placement.includes('bottom')
        }
      : ''
  );

  const Wrapper = withPortal ? FloatingPortal : Fragment;

  const changeFocus = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.code) {
      case ARROW_UP_KEY: {
        event.preventDefault();
        event.stopPropagation();
        const previousElement =
          document.activeElement?.parentElement?.previousElementSibling?.firstElementChild;
        if (previousElement) {
          (previousElement as HTMLButtonElement).focus();
        }
        break;
      }
      case ARROW_DOWN_KEY: {
        event.preventDefault();
        event.stopPropagation();
        const nextElement =
          document.activeElement?.parentElement?.nextElementSibling?.firstElementChild;
        if (nextElement) {
          (nextElement as HTMLButtonElement).focus();
        }

        break;
      }
    }
  };

  return (
    <>
      <Button
        ref={reference}
        variation={variation}
        className={buttonClassName}
        loading={loading}
        disabled={disabled}
        style={css}
        compact={compact}
        {...getReferenceProps({
          onClick: e => {
            e.preventDefault();
            e.stopPropagation();
          }
        })}
      >
        <>
          {label}
          {withIcon && <Icon href={arrowIcon} className={arrowClassName} />}
        </>
      </Button>
      {isMounted && (
        <FloatingFocusManager context={context} guards={false} initialFocus={initialFocus}>
          <Wrapper>
            <div
              ref={floating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: width ?? 'max-content'
              }}
              {...getFloatingProps()}
              className={classnames(
                style.portal,
                {
                  [style.portalWithArrow]: withArrow
                },
                portalPositionnedClassName,
                portalClassName
              )}
              data-status={status}
              onKeyDown={changeFocusOnArrowDown ? changeFocus : undefined}
            >
              <div
                className={classnames(style.dropdown, dropdownClassName)}
                style={{ width }}
                onMouseLeave={onMouseLeave}
              >
                {withArrow && (
                  <div
                    style={{
                      left: arrowX !== undefined ? `${arrowX}px` : '',
                      top: arrowY !== undefined ? `${arrowY}px` : ''
                    }}
                    ref={arrowRef}
                    className={style.triangle}
                  />
                )}
                {children}
              </div>
            </div>
          </Wrapper>
        </FloatingFocusManager>
      )}
    </>
  );
};

Dropdown.displayName = 'Dropdown';

const ItemButton = ({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button type="button" className={classnames(style.itemButton, className)} {...props} />
);
const ItemSeparator = () => <hr className={style.separator} />;

export default Dropdown;
export { ItemButton, ItemSeparator };
